import { IconClose } from '@consta/icons/IconClose'
import { IconInfo } from '@consta/icons/IconInfo'
import { IconIntroduction } from '@consta/icons/IconIntroduction'
import { IconTrash } from '@consta/icons/IconTrash'
import { Button } from '@consta/uikit/Button'
import { Card } from '@consta/uikit/Card'
import { Combobox } from '@consta/uikit/Combobox'
import { Text } from '@consta/uikit/Text'
import { withTooltip } from '@consta/uikit/withTooltip'
import { getSimpleTooltipContent } from '@core/core/helpers'
import {
  applyVelocityDomains,
  getVelocityAllowedTypes,
  getVelocityModelsByActiveProject,
  selectActiveBaseEntity,
  selectActiveProjectId,
  selectAllVelocityDomains,
  selectVelocityAllowedTypes,
  selectVelocityModelsByActiveProject,
  useAppDispatch,
  useAppSelector,
} from '@core/core/store'
import type { BaseEntity, DomainConvertObject } from '@core/core/types'
import type { FC } from 'react'
import { useEffect, useState } from 'react'

import css from './ApplyVelocityModel.css'
import { ObjectList } from './ObjectList'
import type {
  FormApplyVelocityModel,
  OnChangeObjectListFunc,
  PropsApplyVelocityModel as Props,
} from './types'

const ButtonIconInfoWithTooltip = withTooltip({
  content: getSimpleTooltipContent(
    'Список объектов, которые можно преобразовать с помощью расчета. В разработке',
  ),
  size: 'm',
  direction: 'downCenter',
})(Button)

const emptyEntity: BaseEntity = { entityType: 'unknown', entityName: '' }

export const ApplyVelocityModel: FC<Props> = ({ onImportSidebarClose }) => {
  const dispatch = useAppDispatch()
  const velocityModels = useAppSelector(selectVelocityModelsByActiveProject)
  const domains = useAppSelector(selectAllVelocityDomains)
  const { entityType, entityId, entityName } = useAppSelector(selectActiveBaseEntity) ?? emptyEntity
  const activeProjectId = useAppSelector(selectActiveProjectId)
  const velocityAllowedTypes = useAppSelector(state =>
    selectVelocityAllowedTypes(state, activeProjectId),
  )
  const [{ objectList, velocityModel }, setFormData] = useState<FormApplyVelocityModel>({
    velocityModel: null,
    objectList: [],
  })

  const isAllowedTypesForImport = velocityAllowedTypes?.items.includes(entityType)
  const isObjectList = objectList.length
  const isNotObjectInObjectList = !objectList.some(({ id }) => id === entityId)
  const isObjectListChecked = objectList.some(({ checked }) => checked)

  const isDisabledBtnImportObject = !(
    velocityModel &&
    isAllowedTypesForImport &&
    isNotObjectInObjectList
  )
  const isDisabledBtnAllDelete = !isObjectListChecked
  const isDisabledBtnSubmitForm = !(velocityModel && isObjectListChecked)

  useEffect(() => {
    dispatch(getVelocityAllowedTypes(activeProjectId))
    dispatch(getVelocityModelsByActiveProject())
  }, [activeProjectId])

  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      objectList: [
        ...prev.objectList.map(object => {
          const currentObject = domains[0]?.objects?.find(domain => domain.id === object.id)
          return { ...object, error: currentObject?.status === 'Failed', note: currentObject?.note }
        }),
      ],
    }))
  }, [domains])

  const onChangeObjectList: OnChangeObjectListFunc = callback => {
    if (typeof callback === 'function') {
      setFormData(prev => {
        const newObjectList = callback(prev.objectList)
        return {
          ...prev,
          objectList: newObjectList,
        }
      })
    }
  }

  const onChangeVelocityModel = (value: FormApplyVelocityModel['velocityModel']) => {
    setFormData(prev => ({
      ...prev,
      velocityModel: value,
    }))
  }

  const toggleCheckedObjectListAll = (checked: boolean) => () => {
    onChangeObjectList(prev => prev.map(obj => ({ ...obj, checked })))
  }

  const handleDeleteCheckedAllObject = () => {
    onChangeObjectList(prev => prev.filter(({ checked }) => !checked))
  }

  const handleAddObjectInObjectList = () => {
    onChangeObjectList(prev => [
      ...prev,
      {
        id: entityId ?? '',
        name: entityName,
        type: entityType,
        checked: false,
        error: false,
      },
    ])
  }

  const submitForm = () => {
    const objects: DomainConvertObject[] = []
    objectList.forEach(({ id, type, checked }) => {
      if (checked) objects.push({ id, type })
    })
    dispatch(
      applyVelocityDomains({
        projectId: activeProjectId,
        velocityModelId: velocityModel?.id ?? '',
        domain: 'Z',
        objects,
      }),
    )
  }

  return (
    <div className={css.container}>
      <div className={css.wrapper}>
        <Text view="secondary" size="xs" className={css.gsm} weight="semibold">
          Применение ГСМ
        </Text>

        <Button
          size="xs"
          iconRight={IconClose}
          iconSize="xs"
          onlyIcon={true}
          view="clear"
          onClick={onImportSidebarClose}
        />
      </div>

      <div className={css.selectSpeedModelWrapper}>
        <Combobox
          size={'xs' as 's'}
          placeholder="Выберите скоростную модель"
          label="Скоростная модель"
          items={velocityModels}
          value={velocityModel}
          getItemKey={item => item.id}
          getItemLabel={item => item.name}
          onChange={onChangeVelocityModel}
          onClick={() => {
            dispatch(getVelocityModelsByActiveProject())
          }}
        />
      </div>

      <div className={css.objectsRecalculate}>
        <Text view="secondary" size="xs" className={css.recalculateText}>
          Объекты для пересчета
          <ButtonIconInfoWithTooltip
            size="xs"
            view="clear"
            onlyIcon={true}
            iconLeft={() => <IconInfo size="xs" view="ghost" />}
          />
        </Text>

        <Button
          className={css.btnIntroduction}
          size="xs"
          view="ghost"
          onlyIcon={true}
          disabled={isDisabledBtnImportObject}
          iconRight={IconIntroduction}
          iconSize="xs"
          onClick={handleAddObjectInObjectList}
        />

        <Button
          size="xs"
          view="ghost"
          onlyIcon={true}
          disabled={isDisabledBtnAllDelete}
          iconRight={IconTrash}
          iconSize="xs"
          onClick={handleDeleteCheckedAllObject}
        />
      </div>

      <div className={css.buttonsWrapper}>
        <Button
          size="xs"
          view="clear"
          label="Выбрать все"
          disabled={!isObjectList}
          className={`${css.btnAsText} ${css.btnSelectAll}`}
          onClick={toggleCheckedObjectListAll(true)}
        />
        <Button
          size="xs"
          view="clear"
          label="Сбросить"
          disabled={!isObjectList}
          className={css.btnAsText}
          onClick={toggleCheckedObjectListAll(false)}
        />
      </div>

      <Card
        className={`${css.body} ${css.cardWrapper}`}
        verticalSpace="s"
        horizontalSpace="s"
        border={true}
        shadow={false}>
        {isObjectList ? (
          <div className={css.objectListWrapper}>
            <ObjectList items={objectList} onChangeObjectList={onChangeObjectList} />
          </div>
        ) : (
          <Text size="xs" align="center" className={css.capObjectList} view="primary">
            Выделите нужный объект в дереве и нажмите
            <IconIntroduction size="xs" className={css.iconIntroduction} />
          </Text>
        )}
      </Card>

      <Button
        className={css.btnApply}
        size="s"
        view="secondary"
        label="Применить"
        disabled={isDisabledBtnSubmitForm}
        onClick={submitForm}
      />
    </div>
  )
}
